import { useMemo } from 'react';
import { useAuth as useAuthContext } from '@/contexts/AuthContext';
import { usePortalUser } from '@/api/hooks';
import type { PortalUser } from '@/api/types';

type UseAuthOptions = {
  redirectOnUnauthenticated?: boolean;
  redirectPath?: string;
};

/**
 * Portal-compatible auth hook backed by Laravel Sanctum.
 * Maps AuthContext session + /api/auth/user for role-aware portal pages.
 */
export function useAuth(options?: UseAuthOptions) {
  const ctx = useAuthContext();
  const { data: portalUser, isLoading: portalLoading, refetch } = usePortalUser(ctx.isAuthenticated);

  const user: PortalUser | null = useMemo(() => {
    if (portalUser) return portalUser;
    if (!ctx.user) return null;
    return {
      id: ctx.user.id,
      name: ctx.user.name,
      email: ctx.user.email,
      phone: ctx.user.contact?.phone ?? null,
      role:
        ctx.user.role === 'installer' || ctx.user.role === 'admin' || ctx.user.role === 'customer'
          ? ctx.user.role
          : ctx.user.is_admin
            ? 'admin'
            : 'customer',
      hasActiveQuote: Boolean(ctx.user.hasActiveQuote),
      loginMethod: 'email',
    };
  }, [portalUser, ctx.user]);

  return {
    user,
    loading: ctx.isLoading || (ctx.isAuthenticated && portalLoading),
    isAuthenticated: ctx.isAuthenticated,
    logout: async () => {
      await ctx.logout();
      await refetch();
    },
    refresh: () => refetch(),
  };
}

export default useAuth;
